import React from 'react';

import type { FieldsGroupRestrictionPayload, FormField } from '@/forms/typings';
import { atLeastOneChildAnsweredYesKey } from '@/forms/utils/createAtLeastOneChildAnsweredYesYup';
import { atLeastOneChildWithValueKey } from '@/forms/utils/createAtLeastOneChildWithValueYup';
import { FieldError, useFormState } from '../../core';
import { hasGroupRestrictionError, useGroupRestrictionTouch } from '../../hooks';

import './GroupRestrictionErrors.scss';

export interface GroupRestrictionErrorsProps {
  groupFieldId: string;
  restriction?: FieldsGroupRestrictionPayload;
  /** The group's direct children — the same list its restriction schemas were built from. */
  fields?: FormField[];
  /** The group states its rules under its heading (GroupRestrictionHint), so none repeat here. */
  hintAbove?: boolean;
}

/**
 * Renders the validation error slot for the group-scoped restriction kinds, plus the
 * standing explanation of an AtLeastOneChildAnsweredYes rule while it is not failing,
 * unless the group already states it under its heading.
 *
 * RequiredWhenValue is deliberately absent: it names a specific child, so its error
 * renders beside that field via `restrictionErrorKey` (see stampGuardedFieldError).
 * The group still highlights for it — see useGroupRestrictionError.
 */
export const GroupRestrictionErrors: React.FC<GroupRestrictionErrorsProps> = ({
  groupFieldId,
  restriction,
  fields,
  hintAbove = false,
}) => {
  useGroupRestrictionTouch(groupFieldId, fields, restriction);

  const { errors, touched } = useFormState();

  if (!restriction) return null;

  // Narrowed to this one rule: a sibling rule failing must not swallow this hint.
  const answeredYesFailing = hasGroupRestrictionError(errors, touched, groupFieldId, {
    atLeastOneChildAnsweredYes: restriction.atLeastOneChildAnsweredYes,
  });

  return (
    <>
      {restriction.atLeastOneChildWithValue && (
        <FieldError name={atLeastOneChildWithValueKey(groupFieldId)} />
      )}

      {restriction.atLeastOneChildAnsweredYes && (
        <FieldError name={atLeastOneChildAnsweredYesKey(groupFieldId)} />
      )}

      {/* The rule is invisible in the questions themselves, so state it up front; the
          error above says the same thing once it fails, so drop it then. */}
      {restriction.atLeastOneChildAnsweredYes && !hintAbove && !answeredYesFailing && (
        <p className="group-restriction-hint">
          {restriction.atLeastOneChildAnsweredYes.message}
        </p>
      )}
    </>
  );
};
